/**
 * Telling "your credentials are gone" apart from "we could not ask".
 *
 * Only the first justifies sending somebody back to a sign-in page. A network
 * drop or a server fault must never look like being signed out, because that
 * would throw a signed-in person out of their work for no reason.
 */
export interface AuthErrorLike {
  message?: string | null;
  status?: number | null;
  code?: string | null;
  name?: string | null;
}

const DEFINITE_CODES = new Set([
  "session_not_found",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "user_not_found",
  "bad_jwt",
]);

const DEFINITE_MESSAGE = /(auth session missing|session (not found|from session id not found)|session_not_found|refresh[_ ]token[_ ](not found|already used|revoked)|invalid (jwt|claim|refresh token|token)|jwt expired|token is expired|user (not found|from sub claim in jwt does not exist)|not authenticated|no api key found)/i;

/**
 * True only when the backend has definitely said the credentials are missing,
 * expired or invalid.
 */
export function isDefinitelySignedOut(error: AuthErrorLike | null | undefined): boolean {
  if (!error) return false;
  const code = (error.code ?? "").toLowerCase();
  if (DEFINITE_CODES.has(code)) return true;
  if (error.status === 401 || error.status === 403) return true;
  // 0 / undefined status with a fetch failure is a transport problem.
  if (typeof error.status === "number" && error.status >= 500) return false;
  return DEFINITE_MESSAGE.test(error.message ?? "");
}
